import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { APPLICATION_STEPS } from "constants/index";
import { SIZE } from "components/Missions/Applicants/ApplicantMatchScore";
import { ProgressChecklist } from "components/Progress";
import Card from "components/Card";
import { ApplicationRequirementsForm } from "components/Missions/Applicants";
import { margins } from "style";
import Tooltip from "components/Tooltip";
import { TagOptional } from "components/Tags";
import { isApplicationStepOptional } from "utils";

/**
 * ApplicationRequirements
 *
 * @param {Object}    application
 * @param {String}    title
 * @param {Boolean}   showMissingRequirements
 * @param {Boolean}   requirementsEditing
 * @param {Function}  setRequirementsEditing
 */
const ApplicationRequirements = ({
  application,
  title,
  showMissingRequirements,
  requirementsEditing,
  setRequirementsEditing,
  ...props
}) => {
  const { acceptedRequirements, missionRole, acceptedAllRequirements } = application;
  const roleRequirements = missionRole?.requirements;
  const isOptional = isApplicationStepOptional(missionRole?.applicationSteps, APPLICATION_STEPS.requirements);
  const completedRequirements = useMemo(
    () =>
      acceptedRequirements
        .filter((req) => roleRequirements.some((appReq) => appReq.id === req.id))
        .map(({ description, id }) => ({
          description,
          isComplete: true,
          id,
        })),
    [roleRequirements, acceptedRequirements]
  );

  const missingRequirements = useMemo(() => {
    if (!showMissingRequirements) return [];

    return roleRequirements
      .filter((req) => !acceptedRequirements.some((appReq) => appReq.id === req.id))
      .map(({ description, id }) => ({
        description,
        details: acceptedAllRequirements && (
          <Tooltip tooltipId="Missions.Applicants.RoleRequirements.Requirement.AddedLater" />
        ),
        isComplete: false,
        id,
      }));
  }, [roleRequirements, acceptedRequirements, showMissingRequirements, acceptedAllRequirements]);
  const requirements = [...completedRequirements, ...missingRequirements];

  return (
    <>
      <Card>
        <ProgressChecklist
          steps={requirements}
          emptyMsg={<FormattedMessage id="Missions.Applicants.RoleRequirements.EmptyMsg" />}
          subtitle={isOptional && <TagOptional />}
          isEmpty={!acceptedRequirements.length && !missingRequirements.length}
          title={title}
          size={SIZE.small}
          onEdit={() => setRequirementsEditing(true)}
          onAdd={() => setRequirementsEditing(true)}
          marginSize={margins.normal}
          {...props}
        />
      </Card>
      {requirementsEditing && (
        <ApplicationRequirementsForm
          missionRoleApplicant={application}
          requirements={requirements}
          initialValues={{
            acceptedRequirements: completedRequirements.map(({ id, description }) => ({ id, description })),
          }}
          show={requirementsEditing}
          onClose={() => setRequirementsEditing(false)}
        />
      )}
    </>
  );
};

ApplicationRequirements.propTypes = {
  application: PropTypes.object.isRequired,
  title: PropTypes.string,
  showMissingRequirements: PropTypes.bool,
  requirementsEditing: PropTypes.bool,
  setRequirementsEditing: PropTypes.func.isRequired,
};

export default ApplicationRequirements;
